// GSM 03.38 (7-bit) helpers for SMS alert messages.
// AWS End User Messaging bills per message part: 160 chars for a single part,
// 153 per part once split. Any non-GSM char switches the whole message to
// UCS-2 (70 / 67 per part), so alert text is restricted to GSM characters.

export const GSM_SINGLE_PART = 160;
export const GSM_MULTI_PART = 153;

const GSM_BASIC = new Set(
  Array.from(
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
      "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"
  )
);

// Extension table chars: sent with an escape prefix, so each counts as 2
const GSM_EXTENDED = new Set(Array.from("^{}\\[~]|€"));

// Fixed set of non-GSM chars swapped for GSM look-alikes
const GSM_REPLACEMENTS: Record<string, string> = {
  // single quotes / apostrophes
  "‘": "'", "’": "'", "‚": "'", "‛": "'",
  "′": "'", "´": "'", "`": "'",
  // double quotes
  "“": '"', "”": '"', "„": '"', "‟": '"',
  "″": '"', "«": '"', "»": '"',
  // dashes / hyphens / minus
  "‐": "-", "‑": "-", "‒": "-", "–": "-",
  "—": "-", "―": "-", "−": "-",
  // bullets
  "•": "-", "·": "-", "‣": "-", "⁃": "-",
  // ellipsis
  "…": "...",
  // spaces
  "\t": " ", " ": " ", " ": " ", " ": " ", " ": " ",
  " ": " ", " ": " ", " ": " ", " ": " ", " ": " ",
  " ": " ", " ": " ", " ": " ", "　": " ",
  // invisible chars (dropped silently)
  "​": "", "‌": "", "‍": "", "⁠": "", "﻿": "",
  // misc symbols
  "×": "x", "÷": "/", "©": "(c)", "®": "(R)", "™": "TM",
  "¢": "c", "⁄": "/",
  // accented letters without a GSM form
  "á": "a", "â": "a", "ã": "a", "ā": "a", "Á": "A", "À": "A", "Â": "A", "Ã": "A",
  "ç": "c", "ć": "c", "č": "c", "Ć": "C", "Č": "C",
  "ê": "e", "ë": "e", "ē": "e", "È": "E", "Ê": "E", "Ë": "E",
  "í": "i", "î": "i", "ï": "i", "Í": "I", "Ì": "I", "Î": "I", "Ï": "I",
  "ó": "o", "ô": "o", "õ": "o", "Ó": "O", "Ò": "O", "Ô": "O", "Õ": "O",
  "ú": "u", "û": "u", "Ú": "U", "Ù": "U", "Û": "U",
  "ý": "y", "ÿ": "y", "Ý": "Y",
  "š": "s", "Š": "S", "ž": "z", "Ž": "Z",
};

export function isGsmChar(ch: string): boolean {
  return GSM_BASIC.has(ch) || GSM_EXTENDED.has(ch);
}

/** Septets a GSM char occupies (extension chars take 2). */
export function gsmCharWeight(ch: string): number {
  return GSM_EXTENDED.has(ch) ? 2 : 1;
}

/** Billed GSM character count (extension chars count double). */
export function gsmLength(text: string | null | undefined): number {
  let count = 0;
  for (const ch of text ?? "") count += gsmCharWeight(ch);
  return count;
}

/** Number of SMS parts a GSM message is billed as. */
export function gsmPartCount(text: string | null | undefined): number {
  const length = gsmLength(text);
  if (length === 0) return 0;
  return length <= GSM_SINGLE_PART ? 1 : Math.ceil(length / GSM_MULTI_PART);
}

/** Max GSM chars that fit in the given number of parts. */
export function gsmMaxCharsForParts(parts: number): number {
  return parts <= 1 ? GSM_SINGLE_PART : parts * GSM_MULTI_PART;
}

/**
 * Convert text to GSM-only: swap known look-alikes, drop everything else.
 * Returns the cleaned text and the distinct chars that were removed.
 */
export function toGsm(text: string | null | undefined): { text: string; removed: string[] } {
  let result = "";
  const removed = new Set<string>();
  for (const ch of text ?? "") {
    if (isGsmChar(ch)) result += ch;
    else if (ch in GSM_REPLACEMENTS) result += GSM_REPLACEMENTS[ch];
    else removed.add(ch);
  }
  return { text: result, removed: Array.from(removed) };
}
